import { create } from 'zustand';
import { MAX_UNDO_STATES } from '@agentbrush/shared';
import type { QualityLevel } from '@agentbrush/shared';

export interface EditorStore {
  // State
  tool: 'brush' | 'eraser';
  brushSize: number;
  maskData: ImageData | null;
  description: string;
  submissionStatus: 'idle' | 'submitting' | 'success' | 'error';
  resultImageUrl: string | null;
  submissionError: string | null;
  currentImageUrl: string | null;
  undoStack: ImageData[];
  redoStack: ImageData[];
  quality: QualityLevel;
  // True between a successful submit and either an accept or a successful
  // revert. Drives the visibility of the Revert button in the review overlay.
  canRevert: boolean;
  // Number of times the user has clicked Accept on this session. Drives the
  // top-right Finish button (locked at 0, unlocked when > 0) and the
  // beforeunload beacon (only fires when > 0).
  iterationsAccepted: number;
  // True once the session reaches the terminal 'finished' state on the
  // server. Editor inputs read-only after this point.
  sessionFinished: boolean;

  // Actions
  setQuality: (quality: QualityLevel) => void;
  setTool: (tool: 'brush' | 'eraser') => void;
  setBrushSize: (size: number) => void;
  setMaskData: (data: ImageData | null) => void;
  setDescription: (desc: string) => void;
  setSubmissionStatus: (status: EditorStore['submissionStatus']) => void;
  setResultImageUrl: (url: string | null) => void;
  setSubmissionError: (error: string | null) => void;
  setCurrentImageUrl: (url: string | null) => void;
  setCanRevert: (value: boolean) => void;
  // Called by use-accept-result hook after the server returns 200 on /accept.
  // Swaps the result image in as the new base, resets the submission status
  // to 'idle' (composer mode), and bumps iterationsAccepted (unlocks Finish).
  acceptResult: () => void;
  // Local-only dismissal of the review overlay (typing in prompt, painting
  // on mask). No server snapshot, no counter bump. The latest generated
  // image becomes the new base so the next submit edits on top of it.
  dismissReview: () => void;
  keepEditing: () => void;
  markSessionFinished: () => void;
  pushUndo: (snapshot: ImageData) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
}

const BRUSH_SIZE_MIN = 8;
const BRUSH_SIZE_MAX = 256;
const BRUSH_SIZE_DEFAULT = 24;

export const useEditorStore = create<EditorStore>((set) => ({
  tool: 'brush',
  brushSize: BRUSH_SIZE_DEFAULT,
  maskData: null,
  description: '',
  quality: 'medium',
  submissionStatus: 'idle',
  resultImageUrl: null,
  submissionError: null,
  currentImageUrl: null,
  undoStack: [],
  redoStack: [],
  canRevert: false,
  iterationsAccepted: 0,
  sessionFinished: false,

  setQuality: (quality) => set({ quality }),

  setTool: (tool) => set({ tool }),

  setBrushSize: (size) =>
    set({ brushSize: Math.max(BRUSH_SIZE_MIN, Math.min(BRUSH_SIZE_MAX, size)) }),

  setMaskData: (data) => set({ maskData: data }),

  setDescription: (desc) => set({ description: desc }),

  setSubmissionStatus: (status) => set({ submissionStatus: status }),

  setResultImageUrl: (url) => set({ resultImageUrl: url }),

  setSubmissionError: (error) => set({ submissionError: error }),

  setCurrentImageUrl: (url) => set({ currentImageUrl: url }),

  setCanRevert: (value) => set({ canRevert: value }),

  // Server-confirmed accept: the result is snapshotted on R2 as the latest
  // accepted image and the session transitions to 'iterating'. Locally we
  // swap the new base image in, return the dock to composer mode, and bump
  // the accept counter so the Finish button unlocks. canRevert is cleared
  // because the snapshot is now the new starting point.
  acceptResult: () =>
    set((state) =>
      state.submissionStatus === 'success'
        ? {
            currentImageUrl: state.resultImageUrl ?? state.currentImageUrl,
            resultImageUrl: null,
            submissionStatus: 'idle',
            submissionError: null,
            canRevert: false,
            iterationsAccepted: state.iterationsAccepted + 1,
          }
        : state,
    ),

  // Soft dismissal (typing in prompt, painting on mask). No server snapshot —
  // the generated image stays on canvas as the new base but the session is
  // still in 'awaiting_acceptance' on the server, so closing the tab without
  // ever clicking Accept delivers the *previous* accepted image (or 410 if
  // none).
  dismissReview: () =>
    set((state) =>
      state.submissionStatus === 'success'
        ? {
            currentImageUrl: state.resultImageUrl ?? state.currentImageUrl,
            resultImageUrl: null,
            submissionStatus: 'idle',
            submissionError: null,
          }
        : state,
    ),

  keepEditing: () =>
    set((state) => ({
      // Swap the result image in as the new base image. If the caller misuses
      // this action with no pending result, preserve the existing base image
      // rather than wiping it — the description and canvas would otherwise be
      // stranded against a null source.
      currentImageUrl: state.resultImageUrl ?? state.currentImageUrl,
      resultImageUrl: null,
      submissionStatus: 'idle',
      submissionError: null,
      // mask + undo are intentionally preserved so the user can hit Generate
      // again to re-roll the same edit. EditorCanvas drops the preserved
      // bitmap when the new image dimensions differ from the previous canvas
      // (rare now that the API matches OpenAI's output size to the source
      // aspect — square stays square, portrait stays portrait).
      // description is intentionally preserved
    })),

  markSessionFinished: () => set({ sessionFinished: true }),

  pushUndo: (snapshot) =>
    set((state) => {
      const stack = [...state.undoStack, snapshot];
      if (stack.length > MAX_UNDO_STATES) {
        stack.shift();
      }
      return { undoStack: stack, redoStack: [] };
    }),

  undo: () =>
    set((state) => {
      if (state.undoStack.length === 0) return state;
      const stack = [...state.undoStack];
      const snapshot = stack.pop()!;
      return {
        undoStack: stack,
        redoStack: [...state.redoStack, ...(state.maskData ? [state.maskData] : [])],
        maskData: snapshot,
      };
    }),

  redo: () =>
    set((state) => {
      if (state.redoStack.length === 0) return state;
      const stack = [...state.redoStack];
      const snapshot = stack.pop()!;
      return {
        redoStack: stack,
        undoStack: [...state.undoStack, ...(state.maskData ? [state.maskData] : [])],
        maskData: snapshot,
      };
    }),

  reset: () =>
    set({
      tool: 'brush',
      brushSize: BRUSH_SIZE_DEFAULT,
      maskData: null,
      description: '',
      quality: 'medium',
      submissionStatus: 'idle',
      resultImageUrl: null,
      submissionError: null,
      currentImageUrl: null,
      undoStack: [],
      redoStack: [],
      canRevert: false,
      iterationsAccepted: 0,
      sessionFinished: false,
    }),
}));

export { BRUSH_SIZE_MIN, BRUSH_SIZE_MAX, BRUSH_SIZE_DEFAULT };
